import { Controller } from "@hotwired/stimulus"

/**
 * Model Reveal Controller
 *
 * Controls the visibility of model names in competitor cards on the Showdown page.
 * Creates a "blind taste test" experience where users can vote/guess before seeing
 * which model generated each image.
 *
 * Features:
 * - Toggle all model names visible/hidden with a button click
 * - Hover to reveal individual model names (when globally hidden)
 * - Creative scramble/unscramble animation effect
 * - Respects prefers-reduced-motion
 */
export default class extends Controller<HTMLElement> {
  static targets = ["toggle", "toggleIcon", "modelName"]
  static values = {
    revealed: { type: Boolean, default: false }
  }

  declare revealedValue: boolean
  declare readonly toggleTarget: HTMLButtonElement
  declare readonly toggleIconTarget: HTMLElement
  declare readonly modelNameTargets: HTMLElement[]
  declare readonly hasToggleIconTarget: boolean

  private prefersReducedMotion = false

  connect(): void {
    this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    // Initialize all model names as hidden with scrambled text
    this.modelNameTargets.forEach((el) => {
      this.hideModelName(el, false)
    })

    this.updateToggleState()
  }

  // Handle dynamically added model names (e.g. from turbo frame lazy loading)
  modelNameTargetConnected(el: HTMLElement): void {
    if (!el.dataset.realName) {
      el.dataset.realName = el.textContent?.trim() || ""
    }

    if (this.revealedValue) {
      this.revealModelName(el, false)
    } else {
      this.hideModelName(el, false)
    }
  }

  toggle(): void {
    this.revealedValue = !this.revealedValue
    this.updateToggleState()

    this.modelNameTargets.forEach((el) => {
      if (this.revealedValue) {
        this.revealModelName(el, false)
      } else {
        this.hideModelName(el, false)
      }
    })
  }

  // Called when hovering over a model name
  peekReveal(event: Event): void {
    if (this.revealedValue) return // Already revealed globally

    const nameEl = event.currentTarget as HTMLElement
    this.revealModelName(nameEl)
  }

  // Called when mouse leaves a model name
  peekHide(event: Event): void {
    if (this.revealedValue) return // Keep revealed if globally shown

    const nameEl = event.currentTarget as HTMLElement
    this.hideModelName(nameEl, true)
  }

  private updateToggleState(): void {
    if (this.hasToggleIconTarget) {
      this.toggleIconTarget.dataset.revealed = this.revealedValue.toString()
    }
    this.toggleTarget.setAttribute("aria-pressed", this.revealedValue.toString())
  }

  private revealModelName(el: HTMLElement, animate = true): void {
    const realName = el.dataset.realName || ""

    if (!animate || this.prefersReducedMotion) {
      el.textContent = realName
      el.classList.remove("model-name-hidden")
      el.classList.add("model-name-revealed")
      return
    }

    // Animate unscramble
    this.animateText(el, realName, false)
    el.classList.remove("model-name-hidden")
    el.classList.add("model-name-revealed")
  }

  private hideModelName(el: HTMLElement, animate: boolean): void {
    // Store the real name if not already stored
    if (!el.dataset.realName) {
      el.dataset.realName = el.textContent?.trim() || ""
    }

    const scrambled = this.scrambleText(el.dataset.realName || "")

    if (!animate || this.prefersReducedMotion) {
      el.textContent = scrambled
      el.classList.add("model-name-hidden")
      el.classList.remove("model-name-revealed")
      return
    }

    // Animate scramble
    this.animateText(el, scrambled, true)
    el.classList.add("model-name-hidden")
    el.classList.remove("model-name-revealed")
  }

  private scrambleText(text: string): string {
    // Create a visually interesting scrambled version using unicode blocks
    // Mix of block characters that look like redacted text
    const blocks = ["█", "▓", "▒", "░"]
    return text
      .split("")
      .map((char) => {
        if (char === " ") return " "
        return blocks[Math.floor(Math.random() * blocks.length)]
      })
      .join("")
  }

  private animateText(
    el: HTMLElement,
    targetText: string,
    isScrambling: boolean
  ): void {
    const duration = 300
    const steps = 8
    const stepDuration = duration / steps
    const originalText = el.textContent || ""

    let step = 0
    const interval = setInterval(() => {
      step++
      const progress = step / steps

      // For each character, decide if it should be revealed/scrambled based on progress
      const newText = targetText
        .split("")
        .map((targetChar, i) => {
          const sourceChar = originalText[i] || ""

          if (targetChar === " ") return " "

          // Random chance to flip to target character increases with progress
          if (Math.random() < progress) {
            return targetChar
          }

          // Otherwise show intermediate scramble
          if (isScrambling) {
            const blocks = ["█", "▓", "▒", "░"]
            return blocks[Math.floor(Math.random() * blocks.length)]
          } else {
            // When unscrambling, occasionally flash the real character
            if (Math.random() < 0.3) return targetChar
            return sourceChar
          }
        })
        .join("")

      el.textContent = newText

      if (step >= steps) {
        clearInterval(interval)
        el.textContent = targetText
      }
    }, stepDuration)
  }
}
